import React, { useMemo } from "react";
import { Form, Input, Row, Col, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2MultiSelectSelectConfig,
  getTheme2SearchFilterColProps,
  getTheme2SearchFilterMultiSelectStyles,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";

const SEARCH_INVENTORY_STATUS_PREFIX = "search-inventory-status";

const SearchInventoryFilters = ({
  activeColor = "blue",
  isTheme2 = false,
  search,
  onSearchChange,
  status,
  onStatusChange,
  location,
  onLocationChange,
  altLocation,
  onAltLocationChange,
  statusOptions = [],
  locationsData,
  isLoadingStatuses = false,
  showLocationFilters = true,
  showTenantFilter = false,
  onTenantChange,
  tenantValue = null,
  tenantOptions = [],
  isLoadingTenants = false,
  tenantLabel = "Tenant",
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const statusMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );

  const filterSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const statusTheme2Config = useMemo(
    () => getTheme2MultiSelectSelectConfig(SEARCH_INVENTORY_STATUS_PREFIX, isTheme2),
    [isTheme2]
  );

  const locationOptions =
    locationsData?.results?.map((loc) => ({
      value: String(loc.id),
      label: loc.name,
    })) || [];

  const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
    filterColClass: "search-inventory-filter-col",
    md: "3",
  });
  const statusFilterColProps = isTheme2
    ? {
        ...filterColProps,
        className: `${filterColProps.className} search-inventory-filter-col--status`,
      }
    : filterColProps;

  return (
    <Form>
      <Row className={isTheme2 ? "search-inventory-filters-row" : undefined}>
        <Col {...filterColProps}>
          <FormGroup>
            <Label>{t("searchInventory.search")}</Label>
            <Input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("searchInventory.searchPlaceholder")}
              className="form-control"
            />
          </FormGroup>
        </Col>
        <Col {...statusFilterColProps}>
          <FormGroup>
            <Label>{t("searchInventory.status")}</Label>
            <Select
              activeColor={activeColor}
              className={statusTheme2Config.controlClassName}
              placeholder={t("searchInventory.selectStatus")}
              name="status"
              isSearchable
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={statusTheme2Config.hideSelectedOptions}
              value={status}
              onChange={(option) => onStatusChange(option || [])}
              options={statusOptions}
              isClearable
              isDisabled={isLoadingStatuses}
              styles={statusMultiSelectStyles}
              components={statusTheme2Config.components}
              classNames={statusTheme2Config.classNames}
            />
          </FormGroup>
        </Col>
        {showTenantFilter && typeof onTenantChange === "function" ? (
          <Col {...filterColProps}>
            <FormGroup>
              <Label>{tenantLabel}</Label>
              <Select
                activeColor={activeColor}
                className="react-select"
                styles={filterSelectStyles}
                placeholder={tenantLabel}
                name="tenant"
                isSearchable
                closeMenuOnSelect={true}
                value={tenantValue}
                onChange={(option) => onTenantChange(option?.value || "")}
                options={tenantOptions}
                isClearable
                isDisabled={isLoadingTenants}
              />
            </FormGroup>
          </Col>
        ) : null}
        {showLocationFilters ? (
        <>
        <Col {...filterColProps}>
          <FormGroup>
            <Label>{t("searchInventory.location")}</Label>
            <Select
              activeColor={activeColor}
              className="react-select"
              styles={filterSelectStyles}
              placeholder={t("searchInventory.selectLocation")}
              name="location"
              isSearchable
              closeMenuOnSelect={true}
              value={
                location
                  ? {
                      value: location,
                      label:
                        locationsData?.results?.find(
                          (loc) => String(loc.id) === String(location)
                        )?.name || location,
                    }
                  : null
              }
              onChange={(option) => onLocationChange(option?.value)}
              options={locationOptions}
              isClearable
            />
          </FormGroup>
        </Col>
        <Col {...filterColProps}>
          <FormGroup>
            <Label>{t("searchInventory.alternateLocation")}</Label>
            <Select
              activeColor={activeColor}
              className="react-select"
              styles={filterSelectStyles}
              placeholder={t("searchInventory.selectAlternateLocation")}
              name="altLocation"
              isSearchable
              closeMenuOnSelect={true}
              value={
                altLocation
                  ? {
                      value: altLocation,
                      label:
                        locationsData?.results?.find(
                          (loc) => String(loc.id) === String(altLocation)
                        )?.name || altLocation,
                    }
                  : null
              }
              onChange={(option) => onAltLocationChange(option?.value || "")}
              options={locationOptions}
              isClearable
            />
          </FormGroup>
        </Col>
        </>
        ) : null}
      </Row>
    </Form>
  );
};

export default SearchInventoryFilters;
